import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  Chip,
  Box,
  TablePagination,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import EmailStatusSwitch from "./EmailStatusSwitch";
import { renderCellContent } from "./RenderCellContent";
import { DataStoreColumns } from "../../../../Constants/constants";

const DataTable = ({
  data,
  loading,
  rowsPerPage,
  page,
  totalRecords,
  handleChangePage,
  handleChangeRowsPerPage,
  handleDelete,
  fetchData,
}) => {
  return (
    <TableContainer component={Paper} sx={{ mb: 2 }}>
      <Table sx={{ minWidth: 650 }} aria-label="data table">
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.100" }}>
            {DataStoreColumns.map((col, index) => (
              <TableCell key={index} sx={{ fontWeight: "bold" }}>
                {col}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={index}>
                {DataStoreColumns.map((_, colIndex) => (
                  <TableCell key={colIndex}>
                    <Typography variant="body2" color="text.secondary">
                      Loading...
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : data.length > 0 ? (
            data.map((row, i) => (
              <TableRow
                key={i}
                sx={{
                  backgroundColor: row.varified ? "#e8f5e9" : "inherit",
                  "&:hover": {
                    backgroundColor: row.varified ? "#e8f5e9" : "grey.50",
                  },
                }}
              >
                <TableCell>
                  <Chip label={page * rowsPerPage + i + 1} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <EmailStatusSwitch
                    isVerified={row.varified}
                    onDelete={() => handleDelete(row.id)}
                    fetchData={fetchData}
                    rowId={row.id}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={row.event_name || "No event name"}>
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.event_name) || "NA"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BusinessIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.company_name) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.website) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Tooltip title={row.description || "No description"}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {renderCellContent(row.description) || "NA"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    label={renderCellContent(row.employee_count) || "NA"}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.address) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.generic_email) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{renderCellContent(row.booth_number) || "NA"}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.first_name) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.last_name) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={renderCellContent(row.title) || "NA"}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.email) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{renderCellContent(row.linkedin_url) || "NA"}</TableCell>
                <TableCell>{renderCellContent(row.source_url) || "NA"}</TableCell>
                <TableCell>{renderCellContent(row.leads_comment) || "NA"}</TableCell>
                <TableCell>{renderCellContent(row.company_comment) || "NA"}</TableCell>
                <TableCell>
                  <Chip
                    label={renderCellContent(row.country) || "NA"}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={renderCellContent(row.industry) || "NA"}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.person_location) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.company_phone) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>
                      {renderCellContent(row.direct_dial) || "NA"}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap color={row.debouncing_date ? "text.primary" : "text.disabled"}>
                    {row.debouncing_date
                      ? new Date(row.debouncing_date).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                      : "Not run yet"}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={DataStoreColumns.length} align="center">
                <Box sx={{ py: 4 }}>
                  <Typography variant="h6" color="text.secondary">
                    No data available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filters or refresh the page
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={Number(totalRecords)}
        rowsPerPage={Number(rowsPerPage)}
        page={Number(page)}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
        showFirstButton
        showLastButton
      />
    </TableContainer>
  );
};

export default DataTable;
